/**
 * DeveloperDashboard — /dashboard/api-keys
 *
 * The Developer surface for Eventism's Public API (Phase 1 of the
 * AI-native strategy). Lets an organization owner:
 *  - See their current tier + usage graph
 *  - Generate / revoke API keys (raw key revealed one time only)
 *  - Upgrade to Pro (monthly or annual toggle) via Stripe Checkout
 *  - Manage billing via Stripe Portal
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Copy, Key, Trash2, ExternalLink, Zap, TrendingUp, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token') || ''}` } });

const PLAN_COPY = {
  monthly: { price: '$99', cadence: '/month', badge: 'Most popular', note: 'Cancel anytime' },
  annual:  { price: '$999', cadence: '/year', badge: 'Save 15%', note: 'Two months free' },
};

export default function DeveloperDashboard() {
  const [tierInfo, setTierInfo] = useState(null);
  const [usage, setUsage] = useState(null);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [revealedKey, setRevealedKey] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [plan, setPlan] = useState('monthly');
  const [upgrading, setUpgrading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, u, k] = await Promise.all([
        fetch(`${API}/api/api-tier/me`, auth()).then(r => r.json()),
        fetch(`${API}/api/api-tier/usage`, auth()).then(r => r.json()),
        fetch(`${API}/api/api-keys`, auth()).then(r => r.json()),
      ]);
      setTierInfo(t?.detail ? null : t);
      setUsage(u?.detail ? null : u);
      setKeys(k?.keys || []);
    } catch (e) {
      toast.error('Failed to load developer dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isPro = tierInfo?.tier === 'pro' || tierInfo?.tier === 'partner';
  const usagePct = useMemo(() => {
    if (!tierInfo?.quota) return 0;
    return Math.min(100, Math.round((tierInfo.used / tierInfo.quota) * 100));
  }, [tierInfo]);

  const maxDay = useMemo(() => {
    if (!usage?.series?.length) return 1;
    return Math.max(1, ...usage.series.map(x => x.count));
  }, [usage]);

  const createKey = async () => {
    const label = newKeyLabel.trim();
    if (label.length < 2) { toast.error('Give your key a memorable name (2+ chars)'); return; }
    setCreating(true);
    try {
      const r = await fetch(`${API}/api/api-keys`, {
        method: 'POST',
        headers: { ...auth().headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ label, scopes: ['read', 'write'] }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed');
      setRevealedKey(data);
      setNewKeyLabel('');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async () => {
    if (!revokeTarget) return;
    try {
      const r = await fetch(`${API}/api/api-keys/${revokeTarget.key_id}`, {
        method: 'DELETE', headers: auth().headers,
      });
      if (!r.ok) throw new Error('Failed to revoke');
      toast.success('Key revoked');
      setRevokeTarget(null);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const upgrade = async () => {
    setUpgrading(true);
    try {
      const r = await fetch(`${API}/api/api-tier/upgrade`, {
        method: 'POST',
        headers: { ...auth().headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan, origin_url: window.location.origin }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Upgrade failed');
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.message);
      setUpgrading(false);
    }
  };

  const openPortal = async () => {
    try {
      const r = await fetch(`${API}/api/api-tier/portal`, {
        method: 'POST',
        headers: { ...auth().headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ return_url: window.location.href }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Portal failed');
      window.location.href = data.url;
    } catch (e) { toast.error(e.message); }
  };

  const copyToClipboard = (text, label = 'Copied') => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  if (loading) {
    return <div className="p-10 text-center text-slate-500">Loading developer dashboard…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="developer-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-500" />
            <h1 className="text-3xl font-semibold tracking-tight">Developer API</h1>
            <Badge variant="outline" className="ml-1">Phase 1</Badge>
          </div>
          <p className="text-slate-600 mt-1 max-w-2xl">
            Build with the Eventism API and MCP Server. Publish events from any AI agent,
            pull live inventory into your app, and reach 100M+ users nationally.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={isPro ? 'bg-emerald-600' : 'bg-slate-500'} data-testid="tier-badge">
            {tierInfo?.tier_label || 'Free'} tier
          </Badge>
          {isPro && (
            <Button variant="outline" size="sm" onClick={openPortal} data-testid="portal-btn">
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> Manage billing
            </Button>
          )}
        </div>
      </div>

      {/* Tier + usage */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card data-testid="usage-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="w-4 h-4" /> Monthly published events
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-baseline justify-between">
              <div>
                <span className="text-4xl font-semibold" data-testid="usage-used">{tierInfo?.used ?? 0}</span>
                <span className="text-slate-500 ml-2">/ {tierInfo?.quota ?? 0} events</span>
              </div>
              <span className="text-sm text-slate-500">{usagePct}% used</span>
            </div>
            <Progress value={usagePct} />
            {/* Sparkline / bar chart */}
            <div className="pt-3">
              <div className="text-xs text-slate-500 mb-1">This month</div>
              <div className="flex items-end gap-0.5 h-16" data-testid="usage-graph">
                {(usage?.series || []).map((d, i) => (
                  <div
                    key={d.date}
                    className="flex-1 bg-indigo-500/70 hover:bg-indigo-600 rounded-t transition-colors relative group"
                    style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: d.count ? 6 : 2 }}
                    title={`${d.date} — ${d.count} event${d.count === 1 ? '' : 's'}`}
                  >
                    <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] bg-slate-900 text-white px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-10">
                      {d.count} · {d.date.slice(5)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upgrade card — hidden if already Pro */}
        {!isPro ? (
          <Card className="border-indigo-500/40 bg-gradient-to-br from-indigo-950/60 to-slate-900/40 text-slate-100" data-testid="upgrade-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Zap className="w-4 h-4 text-indigo-600" /> Upgrade to Pro
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Plan toggle */}
              <div className="flex bg-slate-800/60 border border-slate-700 rounded-lg p-1" data-testid="plan-toggle">
                {['monthly', 'annual'].map(p => (
                  <button
                    key={p}
                    onClick={() => setPlan(p)}
                    data-testid={`plan-${p}`}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition ${
                      plan === p ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:bg-slate-700/40'
                    }`}
                  >
                    {p === 'monthly' ? 'Monthly' : 'Annual · save 15%'}
                  </button>
                ))}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-semibold">{PLAN_COPY[plan].price}</span>
                <span className="text-slate-400">{PLAN_COPY[plan].cadence}</span>
              </div>
              <ul className="space-y-1.5 text-sm text-slate-300">
                <li>· 100 published events / month</li>
                <li>· Unlimited public reads (search, get, guides)</li>
                <li>· Priority webhook + MCP tool access</li>
                <li>· Featured in the AI‑agent MCP registry</li>
              </ul>
              <Button
                onClick={upgrade}
                disabled={upgrading}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                data-testid="upgrade-btn"
              >
                {upgrading ? 'Redirecting to Stripe…' : `Upgrade — ${PLAN_COPY[plan].price}${PLAN_COPY[plan].cadence}`}
              </Button>
              <p className="text-xs text-slate-400 text-center">{PLAN_COPY[plan].note} · Secured by Stripe</p>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="pro-status-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-4 h-4 text-emerald-600" /> Pro plan active
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-600 space-y-2">
              <div>Subscription: <code className="text-xs">{tierInfo?.stripe_subscription_id || '—'}</code></div>
              {tierInfo?.cancel_at_period_end && (
                <Badge variant="destructive">Scheduled to cancel at period end</Badge>
              )}
              <Button variant="outline" size="sm" onClick={openPortal} data-testid="portal-btn-2">
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Manage billing in Stripe
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* API keys section */}
      <Card data-testid="keys-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-4 h-4" /> API keys
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder='Label (e.g. "Manus integration", "n8n workflow")'
              value={newKeyLabel}
              onChange={e => setNewKeyLabel(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createKey()}
              data-testid="new-key-label"
              className="max-w-md"
            />
            <Button onClick={createKey} disabled={creating} data-testid="create-key-btn">
              {creating ? 'Creating…' : 'Generate key'}
            </Button>
          </div>

          {keys.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center border border-dashed rounded-lg">
              No keys yet. Generate one to start calling the Eventism API.
            </div>
          ) : (
            <div className="border rounded-lg divide-y" data-testid="keys-list">
              {keys.map(k => (
                <div key={k.key_id} className="p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{k.label}</div>
                    <div className="text-xs text-slate-500 font-mono">{k.prefix}…</div>
                  </div>
                  <div className="text-xs text-slate-500 hidden sm:block">
                    {k.last_used_at ? `Last used ${new Date(k.last_used_at).toLocaleDateString()}` : 'Never used'}
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => setRevokeTarget(k)}
                    data-testid={`revoke-${k.key_id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <Separator />
          <div className="text-xs text-slate-500 space-y-1.5">
            <div>REST: <code className="bg-slate-100 px-1 py-0.5 rounded">{'curl -H "Authorization: Bearer YOUR_KEY" '}{API}/api/v1/ping</code></div>
            <div>MCP endpoint: <code className="bg-slate-100 px-1 py-0.5 rounded">{API}/api/mcp</code> · <a className="text-indigo-600" href={`${API}/api/.well-known/mcp`} target="_blank" rel="noreferrer">discovery manifest</a></div>
            <div>OpenAPI 3.1 spec: <a className="text-indigo-600" href={`${API}/api/v1/openapi.json`} target="_blank" rel="noreferrer">{API}/api/v1/openapi.json</a> — drop into Zapier, ChatGPT Actions, Perplexity, n8n</div>
            <div>Full docs: <a className="text-indigo-600" href="https://developer.eventism.com" target="_blank" rel="noreferrer">developer.eventism.com</a></div>
          </div>
        </CardContent>
      </Card>

      {/* Reveal dialog — shows the raw key ONE TIME. */}
      <Dialog open={!!revealedKey} onOpenChange={o => { if (!o) setRevealedKey(null); }}>
        <DialogContent data-testid="reveal-dialog">
          <DialogHeader>
            <DialogTitle>Copy your new API key</DialogTitle>
            <DialogDescription>
              This is the only time you&apos;ll see the full key. Store it somewhere safe —
              you can always generate another later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-slate-900 text-white px-3 py-2 rounded font-mono text-sm break-all" data-testid="revealed-key">
                {revealedKey?.key}
              </code>
              <Button
                variant="outline" size="sm"
                onClick={() => copyToClipboard(revealedKey.key, 'Key copied')}
                data-testid="copy-key-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealedKey(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirm */}
      <AlertDialog open={!!revokeTarget} onOpenChange={o => { if (!o) setRevokeTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this API key?</AlertDialogTitle>
            <AlertDialogDescription>
              Any application using <b>{revokeTarget?.label}</b> will immediately lose access.
              This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={revokeKey} data-testid="confirm-revoke">
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
